/**
 * Shared client-side name matching for user/player search boxes.
 *
 * Mirrors the server-side semantics of `searchOrganizationUsers` (MYS-109):
 * every word in the query must match SOMEWHERE across the searchable fields,
 * rather than each field being tested against the whole raw query.
 *
 * The naive `firstName.includes(q) || lastName.includes(q)` pattern can never
 * match a query containing a space — "Yann P" fails against firstName "Yann"
 * and lastName "Philippe", because neither field alone contains "yann p".
 * Matching per word fixes that and is order-agnostic ("Philippe Yann" works).
 */

export type NameSearchable = {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
};

/**
 * Returns true when every whitespace-separated word of `query` appears in at
 * least one of the user's searchable fields. An empty/whitespace query matches
 * everything, so callers can pass the raw input straight through.
 */
export function matchesNameQuery(
  query: string | null | undefined,
  user: NameSearchable | null | undefined,
): boolean {
  const words = (query ?? '')
    .toLowerCase()
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (words.length === 0) return true;
  if (!user) return false;

  const firstName = user.firstName?.toLowerCase() ?? '';
  const lastName = user.lastName?.toLowerCase() ?? '';
  const email = user.email?.toLowerCase() ?? '';

  // The concatenation lets a multi-word query match across the name boundary
  // ("yann phil" spans both fields); the individual fields are still needed so
  // an email match isn't diluted by the space we inject between the names.
  const haystacks = [firstName, lastName, email, `${firstName} ${lastName}`];

  return words.every((word) => haystacks.some((h) => h.includes(word)));
}
